import { useState, useEffect, useCallback } from "react";
import { apiGet, ApiResponse, ApiError } from "../../services/api.service";

interface Contract {
	id: number;
	event_id: number;
	extra_id: number;
	assistant_id?: number | null;
	status: "awaiting_download" | "viewed" | "downloaded" | "completely_signed" | "downloaded_signed";
	created_at: string;
	updated_at: string;
	first_name: string;
	last_name: string;
	email: string;
	event_name: string;
	event_date: string;
	pdf_size: number;
	has_pdf: boolean;
	is_cancelled?: boolean;
	cancelled_at?: string;
}

interface UseContractsReturn {
	contracts: Contract[];
	loading: boolean;
	error: string | null;
	refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch user contracts from the API
 * @returns User contracts data, loading state, error state, and refetch function
 */
export function useContracts(): UseContractsReturn {
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	const fetchContracts = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<Contract[]> = await apiGet<Contract[]>(
				"/contracts/user",
				undefined,
				{
					notification: {
						enabled: false, // Silent fetch, no notifications
					},
				}
			);

			if (response.success && response.data) {
				// Filter out cancelled contracts as an additional safety measure
				const activeContracts = response.data.filter(contract => !contract.is_cancelled);
				setContracts(activeContracts);
			} else {
				setError(response.error || "Fehler beim Abrufen der Verträge");
				setContracts([]);
			}
		} catch (err) {
			if (err instanceof ApiError) {
				setError(err.message);
			} else if (err instanceof Error) {
				setError(err.message);
			} else {
				setError("Ein unerwarteter Fehler ist beim Abrufen der Verträge aufgetreten");
			}
			setContracts([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchContracts();
	}, [fetchContracts]);

	return {
		contracts,
		loading,
		error,
		refetch: fetchContracts,
	};
}